import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Loader2, RefreshCw, Share2, Wand2 } from "lucide-react";

const B = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const MakeFlyer = ({ item, mode, onChange }) => {
  const [status, setStatus] = useState(item.flyer_status);
  const make = async () => {
    setStatus("building");
    try {
      await axios.post(`${B}/api/${mode === "admin" ? "admin/dealer-content" : "dealer/content"}/events/${item.id}/flyer`, {}, auth());
      toast.success("Making the flyer. It takes about a minute.");
      setTimeout(() => onChange?.(), 60000);
    } catch (e) { setStatus("failed"); toast.error(e.response?.data?.detail || "Couldn't start the flyer"); }
  };
  const building = status === "building";
  return (
    <div className="flex items-center gap-2 flex-wrap text-xs" data-testid={`event-flyer-pending-${item.id}`}>
      <span className={status === "failed" ? "text-red-600" : "text-slate-400"}>
        {building ? "Flyer is being created… about a minute." : status === "failed" ? "The flyer couldn't be made." : "No flyer yet."}
      </span>
      {building
        ? onChange && <Button type="button" size="sm" variant="ghost" onClick={onChange} data-testid={`event-flyer-refresh-${item.id}`}><RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh</Button>
        : <Button type="button" size="sm" variant="outline" onClick={make} data-testid={`event-flyer-make-${item.id}`}><Wand2 className="w-3.5 h-3.5 mr-1" />{status === "failed" ? "Try again" : "Make flyer"}</Button>}
    </div>
  );
};

// Event flyer preview + download/share; offers "Make flyer" when an event has none (older events or failures)
export const EventFlyer = ({ item, compact = false, mode = "admin", onChange }) => {
  const [busy, setBusy] = useState(false);
  if (!item.flyer_url) return <MakeFlyer item={item} mode={mode} onChange={onChange} />;
  const src = `${B}${item.flyer_url}`;
  const fileName = `${(item.title || "event").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-flyer.jpg`;
  const share = async () => {
    const url = item.blog_slug && item.status === "approved" ? `${window.location.origin}/blog/${item.blog_slug}` : window.location.origin;
    const text = `${item.title} at ${item.location_name || "Total Wireless"}! Come see us.`;
    try {
      const blob = await (await fetch(src)).blob();
      const file = new File([blob], fileName, { type: "image/jpeg" });
      if (navigator.canShare?.({ files: [file] })) { await navigator.share({ files: [file], title: item.title, text: `${text} ${url}` }); return; }
    } catch (e) { if (e?.name === "AbortError") return; }
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, "_blank", "noopener,width=640,height=560");
    toast.info("For Instagram, download the flyer and post it from your phone.");
  };
  const download = async () => {
    setBusy(true);
    try {
      const blob = await (await fetch(src)).blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Download failed"); } finally { setBusy(false); }
  };
  return (
    <div className={compact ? "flex items-center gap-2" : "space-y-2"}>
      <a href={src} target="_blank" rel="noreferrer"><img src={src} alt={`${item.title} flyer`} className={compact ? "w-10 h-12 object-cover rounded" : "w-full rounded-lg border border-slate-200"} data-testid={`event-flyer-${item.id}`} /></a>
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" variant="outline" onClick={download} disabled={busy} data-testid={`event-flyer-download-${item.id}`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}{!compact && <span className="ml-1">Download</span>}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={share} data-testid={`event-flyer-share-${item.id}`}>
          <Share2 className="w-4 h-4" />{!compact && <span className="ml-1">Share</span>}
        </Button>
      </div>
    </div>
  );
};
